import { useRef } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";
import { Download, CreditCard, Ban } from "lucide-react";
import { BookMockup } from "./BookMockup";
import { BuyButton } from "./BuyButton";
import { MaskedLine, FadeIn } from "./motion/Reveal";

const TONES = [
  { id: "warm", label: "Warm", dot: "bg-amber-500/80" },
  { id: "balanced", label: "Balanced", dot: "bg-emerald-500/70" },
  { id: "firm", label: "Firm", dot: "bg-rose-500/70" },
];

export function Hero() {
  const tiltRef = useRef(null);
  const rx = useSpring(useMotionValue(0), { stiffness: 140, damping: 18 });
  const ry = useSpring(useMotionValue(0), { stiffness: 140, damping: 18 });

  const onMove = (e) => {
    const rect = tiltRef.current?.getBoundingClientRect();
    if (!rect) return;
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    ry.set(px * 12);
    rx.set(-py * 9);
  };
  const onLeave = () => {
    rx.set(0);
    ry.set(0);
  };

  return (
    <section id="top" className="relative overflow-hidden pt-32 sm:pt-40">
      {/* faint editorial grid backdrop */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(20,19,17,0.035) 1px, transparent 1px)",
          backgroundSize: "clamp(48px, 8vw, 120px) 100%",
        }}
      />
      <div className="relative mx-auto grid max-w-6xl grid-cols-1 items-center gap-16 px-4 pb-20 sm:px-6 lg:grid-cols-12 lg:gap-8 lg:px-8 lg:pb-28">
        <div className="lg:col-span-7">
          <FadeIn delay={0.15}>
            <div className="flex items-center gap-3">
              <span className="h-px w-10 bg-ink/40" />
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-smoke">
                SayIt Manager Edition
              </p>
            </div>
          </FadeIn>
          <h1
            data-testid="hero-headline"
            className="mt-8 font-display text-[2.65rem] font-light leading-[1.05] tracking-tight text-ink sm:text-6xl lg:text-[4.35rem]"
          >
            <MaskedLine delay={0.25}>Know what you</MaskedLine>
            <MaskedLine delay={0.35}>
              need to <em className="font-normal not-italic italic">say.</em>
            </MaskedLine>
            <MaskedLine delay={0.5} className="mt-3 text-[0.62em] text-smoke">
              Stop struggling with
            </MaskedLine>
            <MaskedLine delay={0.6} className="text-[0.62em] text-smoke">
              how to say it.
            </MaskedLine>
          </h1>
          <FadeIn delay={0.75}>
            <p className="mt-8 max-w-md text-base leading-relaxed text-smoke sm:text-lg">
              100 ready-to-use messages for the difficult conversations every
              manager eventually faces.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-2.5">
              {TONES.map((t) => (
                <span
                  key={t.id}
                  data-testid={`hero-tone-chip-${t.id}`}
                  className="inline-flex items-center gap-2 rounded-full border border-line bg-white/70 px-4 py-1.5 text-sm text-ink"
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${t.dot}`} />
                  {t.label}
                </span>
              ))}
            </div>
            <div className="mt-10">
              <BuyButton
                testid="hero-cta-button"
                source="hero"
                label="Get all 100 messages"
              />
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-[13px] text-faint">
              <span className="inline-flex items-center gap-1.5">
                <Download className="h-3.5 w-3.5" /> Instant digital download
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CreditCard className="h-3.5 w-3.5" /> One-time payment
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Ban className="h-3.5 w-3.5" /> No subscription
              </span>
            </div>
          </FadeIn>
        </div>

        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.55 }}
            className="relative mx-auto w-64 sm:w-80"
          >
            {/* clipped frame */}
            <div
              aria-hidden="true"
              className="absolute -inset-5 -z-10 rounded-2xl border border-line bg-surface/60"
            />
            {/* spotlight */}
            <div
              aria-hidden="true"
              className="absolute -inset-16 -z-10 rounded-full"
              style={{
                background:
                  "radial-gradient(closest-side, rgba(180,83,9,0.10), rgba(250,249,246,0))",
              }}
            />
            <div
              ref={tiltRef}
              data-testid="hero-product-mockup"
              onMouseMove={onMove}
              onMouseLeave={onLeave}
              style={{ perspective: 1200 }}
            >
              <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}>
                <BookMockup />
              </motion.div>
            </div>
            {/* floor shadow */}
            <div
              aria-hidden="true"
              className="mx-auto mt-6 h-5 w-3/4 rounded-[50%] bg-ink/15 blur-md"
            />
            {/* floating spec chips */}
            <motion.div
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -left-16 top-10 hidden rounded-lg border border-line bg-white/90 px-3.5 py-2 shadow-sm backdrop-blur sm:block"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-faint">
                Situations
              </p>
              <p className="font-display text-xl text-ink">100</p>
            </motion.div>
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              className="absolute -right-14 bottom-16 hidden rounded-lg border border-line bg-white/90 px-3.5 py-2 shadow-sm backdrop-blur sm:block"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-faint">
                Message versions
              </p>
              <p className="font-display text-xl text-ink">300</p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
